/**
 * Glass transparency — one home for turning the user's 0..1 transparency setting
 * (App & Account → Glass transparency) into the actual alpha used by the glass
 * surfaces, and for pushing it live onto the page.
 *
 * The mapping is deliberately clamped: even at maximum transparency the surface
 * keeps enough alpha (plus its backdrop blur) that labels don't wash out over a
 * busy chart. Higher transparency = lower alpha = more see-through.
 */

/** Surface alpha at transparency 0 (most solid) and 1 (most see-through). */
export const MENU_ALPHA_SOLID = 0.94;
export const MENU_ALPHA_CLEAR = 0.68;

/** The CSS custom property the menu/sheet glass reads for its background alpha. */
export const GLASS_ALPHA_VAR = '--acp-menu-alpha';

/** Clamp a transparency setting into 0..1. */
export function clampTransparency(t: number): number {
  if (!Number.isFinite(t)) return 0.6;
  return Math.min(1, Math.max(0, t));
}

/** Map a 0..1 transparency setting to the surface alpha (clamped, legible). */
export function glassAlpha(transparency: number): number {
  const t = clampTransparency(transparency);
  return MENU_ALPHA_SOLID - t * (MENU_ALPHA_SOLID - MENU_ALPHA_CLEAR);
}

/**
 * Push the transparency live onto the page by setting the alpha CSS variable on
 * the document root, where the .acp-shell glass surfaces inherit it. No-op
 * outside the browser (SSR / tests).
 */
export function applyGlass(transparency: number): void {
  if (typeof document === 'undefined') return;
  document.documentElement.style.setProperty(GLASS_ALPHA_VAR, glassAlpha(transparency).toFixed(3));
}
